import React, { useMemo } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScrollToTop";
import { useAsideMenu } from "../../../../contexts/AsideMenuContext";

import useTasksKanban from "./hooks/useTasksKanban";

import TaskKanbanHeader from "./components/TaskKanbanHeader";
import TaskKanbanToolbar from "./components/TaskKanbanToolbar";
import TaskKanbanBoard from "./components/TaskKanbanBoard";
import TaskDetailModal from "./components/TaskDetailModal";
import TaskDeleteModal from "./components/TaskDeleteModal";
import TaskQuickCreateCard from "./components/TaskQuickCreateCard";

export default function ListingTaches() {
  useScrollToTop();

  const { isOpen } = useAsideMenu();
  const kanban = useTasksKanban();

  const asideWidth = isOpen ? 290 : 92;

  const fullBoardStyle = useMemo(
    () => ({
      width: `calc(100vw - ${asideWidth}px)`,
      marginLeft: `calc(50% - 50vw + ${asideWidth}px)`,
    }),
    [asideWidth],
  );

  return (
    <div
      className="relative min-h-[calc(100vh-76px)] overflow-hidden bg-slate-950 text-slate-900"
      style={fullBoardStyle}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_18%,rgba(56,189,248,0.28),transparent_30%),radial-gradient(circle_at_82%_14%,rgba(168,85,247,0.24),transparent_28%),radial-gradient(circle_at_48%_105%,rgba(250,204,21,0.16),transparent_35%),linear-gradient(135deg,#020617_0%,#07111f_42%,#111827_100%)]" />
      <div className="absolute inset-x-0 top-0 h-32 bg-white/5 blur-3xl" />
      <div className="absolute inset-x-0 bottom-0 h-72 bg-[radial-gradient(ellipse_at_bottom,rgba(125,211,252,0.20),transparent_65%)]" />

      <div className="relative z-10 flex min-h-[calc(100vh-76px)] flex-col">
        <div className="border-b border-white/10 bg-slate-950/75 shadow-2xl backdrop-blur-xl">
          <TaskKanbanHeader
            board={kanban.board}
            stats={kanban.stats}
            loading={kanban.loading}
            onRefresh={kanban.loadBoard}
            onOpenCreate={kanban.openCreateTask}
          />
        </div>

        <div className="border-b border-white/10 bg-white/[0.08] px-4 py-3 backdrop-blur-xl">
          <TaskKanbanToolbar
            filters={kanban.filters}
            updateFilter={kanban.updateFilter}
            resetFilters={kanban.resetFilters}
            productsApps={kanban.productsApps}
            totalColumns={kanban.columns.length}
            visibleColumns={kanban.filteredColumns.length}
            allCardsExpanded={kanban.allCardsExpanded}
            onToggleAllCards={kanban.toggleAllCards}
          />
        </div>

        <TaskKanbanBoard
          columns={kanban.filteredColumns}
          loading={kanban.loading}
          onDragEnd={kanban.handleDragEnd}
          onOpenTask={kanban.setSelectedTask}
          onCreateInColumn={kanban.setCreateColumn}
          onToggleDone={kanban.handleToggleDone}
          onDuplicate={kanban.handleDuplicateTask}
          onArchive={kanban.handleArchiveTask}
          onDelete={kanban.setTaskToDelete}
          allCardsExpanded={kanban.allCardsExpanded}
          expandedTaskIds={kanban.expandedTaskIds}
          onToggleTaskExpanded={kanban.toggleTaskExpanded}
        />
      </div>

      {kanban.createColumn && (
        <TaskQuickCreateCard
          column={kanban.createColumn}
          associations={kanban.associations}
          productsApps={kanban.productsApps}
          saving={kanban.saving}
          onClose={() => kanban.setCreateColumn(null)}
          onCreate={async (formData) => {
            const created = await kanban.handleCreateTask(formData);
            if (created) kanban.setCreateColumn(null);
          }}
        />
      )}

      {kanban.selectedTask && (
        <TaskDetailModal
          task={kanban.selectedTask}
          associations={kanban.associations}
          productsApps={kanban.productsApps}
          saving={kanban.saving}
          onClose={() => kanban.setSelectedTask(null)}
          onUpdate={kanban.handleUpdateTask}
          onToggleDone={kanban.handleToggleDone}
          onDuplicate={kanban.handleDuplicateTask}
          onArchive={kanban.handleArchiveTask}
          onDelete={kanban.setTaskToDelete}
        />
      )}

      {kanban.taskToDelete && (
        <TaskDeleteModal
          task={kanban.taskToDelete}
          onClose={() => kanban.setTaskToDelete(null)}
          onConfirm={() => kanban.handleDeleteTask(kanban.taskToDelete)}
        />
      )}

      <ToastContainer position="bottom-right" />
    </div>
  );
}